const API_BASE_URL = "https://public-real-estate-ingestor.torahmike.workers.dev";

export type PropertyStatus = "active" | "removed" | string;

export interface PropertySummary {
  id: string;
  price: number | null;
  rooms: number | null;
  sqm: number | null;
  city: string | null;
  neighborhood: string | null;
  floor: number | null;
  description: string | null;
  status: PropertyStatus;
  first_seen_at: string;
  last_updated_at: string;
  freshness_score: number;
}

export interface PropertyMember {
  id: string;
  source_type: "public" | "broker";
  source_url: string | null;
  price: number | null;
  rooms: number | null;
  sqm: number | null;
  city: string | null;
  neighborhood: string | null;
  floor: number | null;
  description: string | null;
  status: PropertyStatus;
  created_at: string;
  updated_at: string;
}

export interface PropertyDetail extends PropertySummary {
  clusterMembers: PropertyMember[];
}

export interface FetchPropertiesParams {
  page?: number;
  pageSize?: number;
  city?: string;
  neighborhood?: string;
  rooms?: number;
  minPrice?: number;
  maxPrice?: number;
}

export interface PaginatedProperties {
  data: PropertySummary[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface Distribution {
  min: number | null;
  p25: number | null;
  median: number | null;
  p75: number | null;
  max: number | null;
}

export interface NeighborhoodStats {
  city: string | null;
  neighborhood: string | null;
  medianPrice: number | null;
  pricePerSqmDistribution: Distribution;
  roomCountDistribution: Record<string, number>;
  listingVolume: number;
  timeOnMarketEstimatesDays: Distribution;
}

export interface BrokerInventoryItem {
  url?: string;
  price?: number | string | null;
  rooms?: number | string | null;
  sqm?: number | string | null;
  city?: string | null;
  neighborhood?: string | null;
  floor?: number | string | null;
  description?: string | null;
}

export type BrokerInventoryPayload =
  | BrokerInventoryItem
  | BrokerInventoryItem[]
  | string
  | Blob;

export type BrokerUploadResult =
  | { ok: true; canonicalListingId: string }
  | { ok: false; errors: string[] };

export interface BrokerUploadResponse {
  results: BrokerUploadResult[];
}

export interface BrokerOverlap {
  exclusiveToBroker: string[];
  missingFromBroker: string[];
  sharedByMany: string[];
}

export interface CooperationSignals {
  brokerCount: number;
  inquiryCount: number;
  updateCount: number;
  listingFreshness: string;
  viewerBrokerId?: string;
}

export interface LifecycleEvent {
  event_type: "created" | "price_change" | "status_change" | "updated";
  old_value: string | null;
  new_value: string | null;
  occurred_at: string;
}

export interface PropertyLifecycle {
  firstSeenDate: string;
  lastUpdatedDate: string;
  priceChanges: LifecycleEvent[];
  statusChanges: LifecycleEvent[];
}

interface ApiErrorBody {
  error?: string;
}

export class Mls2ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "Mls2ApiError";
    this.status = status;
  }
}

export async function fetchProperties(
  params: FetchPropertiesParams = {},
): Promise<PaginatedProperties> {
  return apiFetch<PaginatedProperties>("/properties" + toSearchParams(params));
}

export function fetchProperty(id: string): Promise<PropertyDetail> {
  return apiFetch<PropertyDetail>("/properties/" + encodeURIComponent(id));
}

export function fetchNeighborhoodStats(
  city?: string,
  neighborhood?: string,
): Promise<NeighborhoodStats> {
  const query = toSearchParams({ city, neighborhood });
  return apiFetchWithNotFoundFallback<NeighborhoodStats>(
    "/analytics/neighborhood" + query,
    "/stats/neighborhood" + query,
  );
}

export function uploadBrokerInventory(
  jwt: string,
  jsonOrCsv: BrokerInventoryPayload,
): Promise<BrokerUploadResponse> {
  const isCsv = typeof jsonOrCsv === "string" || jsonOrCsv instanceof Blob;
  const body = isCsv ? jsonOrCsv : JSON.stringify(jsonOrCsv);

  return apiFetch<BrokerUploadResponse>("/broker/upload", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + jwt,
      "Content-Type": isCsv ? "text/csv" : "application/json",
    },
    body,
  });
}

export function fetchBrokerOverlap(jwt: string): Promise<BrokerOverlap> {
  return apiFetch<BrokerOverlap>("/broker/overlap", {
    headers: { Authorization: "Bearer " + jwt },
  });
}

export function fetchCooperationSignals(
  id: string,
  jwt?: string,
): Promise<CooperationSignals> {
  const encodedId = encodeURIComponent(id);
  const init = {
    headers: jwt ? { Authorization: "Bearer " + jwt } : undefined,
  } satisfies RequestInit;

  return apiFetchWithNotFoundFallback<CooperationSignals>(
    "/properties/" + encodedId + "/cooperation",
    "/cooperation/" + encodedId,
    init,
  );
}

export function fetchLifecycle(id: string): Promise<PropertyLifecycle> {
  const encodedId = encodeURIComponent(id);
  return apiFetchWithNotFoundFallback<PropertyLifecycle>(
    "/properties/" + encodedId + "/lifecycle",
    "/lifecycle/" + encodedId,
  );
}

async function apiFetchWithNotFoundFallback<T>(
  primaryPath: string,
  fallbackPath: string,
  init?: RequestInit,
): Promise<T> {
  try {
    return await apiFetch<T>(primaryPath, init);
  } catch (error) {
    if (!(error instanceof Mls2ApiError) || error.status !== 404) throw error;
    return apiFetch<T>(fallbackPath, init);
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(API_BASE_URL + path, init);

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new Mls2ApiError(
      response.status,
      body.error ?? "MLS2 API request failed with status " + response.status,
    );
  }

  return response.json() as Promise<T>;
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    return (await response.json()) as ApiErrorBody;
  } catch {
    return {};
  }
}

function toSearchParams(
  values: object,
): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(values)) {
    if ((typeof value === "string" && value !== "") || typeof value === "number") {
      params.set(key, String(value));
    }
  }

  const query = params.toString();
  return query ? "?" + query : "";
}